/**
 * Phrase Card Component
 * Display phrase with usage scenario and examples
 */

import { useState } from 'react';
import { Phrase, phraseCategories } from '../../data/phrases-types';
import { ttsService } from '../../services/tts';
import { getDisplayPhonetic, getPhoneticComparison } from '../../utils/phonetic-converter';

interface PhraseCardProps {
  phrase: Phrase;
  showTranslation?: boolean;
  onLearned?: (phrase: Phrase) => void;
}

export default function PhraseCard({ phrase, showTranslation = true, onLearned }: PhraseCardProps) {
  const [playingAccent, setPlayingAccent] = useState<'american' | 'british' | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [playingExample, setPlayingExample] = useState(false);

  const playPronunciation = async (accent: 'american' | 'british') => {
    if (playingAccent) return;

    setPlayingAccent(accent);
    const previousAccent = ttsService.getAccent();
    ttsService.setAccent(accent);

    try {
      await ttsService.speakNormal(phrase.phrase);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      ttsService.setAccent(previousAccent);
      setPlayingAccent(null);
    }
  };

  const playExample = async () => {
    if (playingExample) return;

    setPlayingExample(true);
    try {
      await ttsService.speakNormal(phrase.example);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      setPlayingExample(false);
    }
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getFrequencyText = (frequency: string) => {
    switch (frequency) {
      case 'high':
        return '高频';
      case 'medium':
        return '中频';
      case 'low':
        return '低频';
      default:
        return '';
    }
  };

  // Get phonetics with automatic conversion
  const getAmericanPhonetic = () => {
    return getDisplayPhonetic(phrase, 'american');
  };

  const getBritishPhonetic = () => {
    return getDisplayPhonetic(phrase, 'british');
  };

  const phoneticComparison = getPhoneticComparison(phrase);

  return (
    <div className="bg-white rounded-2xl shadow-soft p-6 hover:shadow-lg transition-all">
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-2xl font-bold text-gray-800">{phrase.phrase}</h3>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              phrase.level === 'elementary' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
            }`}>
              {phrase.level === 'elementary' ? '小学' : '初中'}
              {phrase.grade && ` ${phrase.grade}年级`}
            </span>
            {phrase.frequency && (
              <span className={`px-2 py-1 rounded text-xs font-medium ${getFrequencyColor(phrase.frequency)}`}>
                {getFrequencyText(phrase.frequency)}
              </span>
            )}
          </div>

          {/* Phonetics - American and British */}
          <div className="space-y-2">
            {/* American English */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => playPronunciation('american')}
                className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
                  playingAccent === 'american'
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                }`}
                title="播放美式发音"
              >
                <span className="text-lg">🇺🇸</span>
                <span className="text-sm">美式</span>
                <span className="text-lg">
                  {playingAccent === 'american' ? '🔊' : '▶️'}
                </span>
              </button>
              <span className="text-sm font-mono text-gray-600">
                {getAmericanPhonetic()}
              </span>
              {phoneticComparison.isDifferent && (
                <span className="text-xs text-blue-600 font-medium">不同</span>
              )}
            </div>

            {/* British English */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => playPronunciation('british')}
                className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
                  playingAccent === 'british'
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-50 hover:bg-purple-100 text-purple-700'
                }`}
                title="播放英式发音"
              >
                <span className="text-lg">🇬🇧</span>
                <span className="text-sm">英式</span>
                <span className="text-lg">
                  {playingAccent === 'british' ? '🔊' : '▶️'}
                </span>
              </button>
              <span className="text-sm font-mono text-gray-600">
                {getBritishPhonetic()}
              </span>
              {phoneticComparison.isDifferent && (
                <span className="text-xs text-purple-600 font-medium">不同</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Meaning */}
      <div className="mb-4">
        <p className="text-lg text-gray-700 font-medium">{phrase.meaning}</p>
      </div>

      {/* Usage Scenario - Highlighted */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-3 border-l-4 border-primary">
        <div className="flex items-start gap-2">
          <span className="text-2xl">💡</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-1">使用场景</p>
            <p className="text-gray-700">{phrase.usageScenario}</p>
          </div>
        </div>
      </div>

      {/* Example sentence */}
      {showTranslation && (
        <div className="bg-gray-50 rounded-xl p-4 mb-3">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-gray-700 mb-1">📝 {phrase.example}</p>
              {phrase.exampleTranslation && (
                <p className="text-sm text-gray-500">{phrase.exampleTranslation}</p>
              )}
            </div>
            <button
              onClick={playExample}
              disabled={playingExample}
              className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors ${
                playingExample
                  ? 'bg-green-500 text-white'
                  : 'bg-green-50 hover:bg-green-100 text-green-700'
              }`}
              title="播放例句"
            >
              <span className="text-lg">
                {playingExample ? '🔊' : '▶️'}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Difficulty and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            phrase.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
            phrase.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {phrase.difficulty === 'easy' ? '简单' : phrase.difficulty === 'medium' ? '中等' : '困难'}
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {phraseCategories.find(c => c.id === phrase.category)?.name || phrase.category}
          </span>
        </div>

        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-primary hover:underline"
        >
          {showDetails ? '收起' : '详情'}
        </button>
      </div>

      {/* Detailed information */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">分类：</span>
              <span className="text-gray-700">{phraseCategories.find(c => c.id === phrase.category)?.name || phrase.category}</span>
            </div>
            {phrase.grade && (
              <div>
                <span className="text-gray-500">年级：</span>
                <span className="text-gray-700">{phrase.grade}年级</span>
              </div>
            )}
            {phrase.level && (
              <div>
                <span className="text-gray-500">学段：</span>
                <span className="text-gray-700">{phrase.level === 'elementary' ? '小学' : '初中'}</span>
              </div>
            )}
            {phrase.frequency && (
              <div>
                <span className="text-gray-500">频率：</span>
                <span className="text-gray-700">{getFrequencyText(phrase.frequency)}</span>
              </div>
            )}
          </div>

          {onLearned && (
            <button
              onClick={() => onLearned(phrase)}
              className="mt-3 w-full py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              我学会了 ✅
            </button>
          )}
        </div>
      )}
    </div>
  );
}
